<template>
  <div class="logo-upload">
    <div v-if="hasCustomLogo" class="logo-preview">
      <img :src="effectiveLogo" :alt="$t('pages.settings.branding.logo.currentLogoAlt')">
    </div>
    <div v-else class="logo-placeholder">
      {{ $t('pages.settings.branding.logo.noCustomLogo') }}
    </div>

    <input
      ref="fileInput"
      type="file"
      accept="image/png"
      class="hidden-input"
      @change="onFileSelected"
    >

    <div class="actions">
      <ib-button :loading="busy" @click="onUploadClick">
        {{ $t('pages.settings.branding.logo.uploadButton') }}
      </ib-button>

      <el-link
        v-if="hasCustomLogo"
        type="primary"
        :underline="false"
        :disabled="busy"
        @click="onReset"
      >
        {{ $t('pages.settings.branding.logo.resetToDefault') }}
      </el-link>
    </div>

    <p v-if="errorMessage" class="error">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

const MAX_BYTES = 2 * 1024 * 1024

export default {
  name: 'LogoUpload',

  data () {
    return {
      busy: false,
      errorMessage: ''
    }
  },

  computed: {
    ...mapGetters('appSettings', ['effectiveLogo', 'hasCustomLogo'])
  },

  methods: {
    ...mapActions('appSettings', ['uploadLogo', 'removeLogo']),

    onUploadClick () {
      this.errorMessage = ''
      this.$refs.fileInput.click()
    },

    async onFileSelected (e) {
      const file = e.target.files[0]
      if (!file) return

      if (file.type !== 'image/png') {
        this.errorMessage = this.$t('pages.settings.branding.logo.invalidType')
        this.$refs.fileInput.value = ''

        return
      }

      if (file.size > MAX_BYTES) {
        this.errorMessage = this.$t('pages.settings.branding.logo.tooLarge')
        this.$refs.fileInput.value = ''

        return
      }

      this.errorMessage = ''
      this.busy = true
      try {
        await this.uploadLogo(file)
        this.$notify({
          title: this.$t('success'),
          message: this.$t('pages.settings.branding.logo.uploadedMessage'),
          type: 'success'
        })
      } catch (err) {
        this.$notify({
          title: this.$t('error'),
          message: this.$t('pages.settings.branding.logo.uploadFailed'),
          type: 'error'
        })
      } finally {
        this.busy = false
        this.$refs.fileInput.value = ''
      }
    },

    async onReset () {
      this.errorMessage = ''
      this.busy = true
      try {
        await this.removeLogo()
        this.$notify({
          title: this.$t('success'),
          message: this.$t('pages.settings.branding.logo.resetMessage'),
          type: 'success'
        })
      } catch (err) {
        this.$notify({
          title: this.$t('error'),
          message: this.$t('pages.settings.branding.logo.resetFailed'),
          type: 'error'
        })
      } finally {
        this.busy = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.logo-upload {
  margin-top: 8px;
}

.logo-preview,
.logo-placeholder {
  width: 240px;
  height: 96px;
  border: 1px solid #eef0f4;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafbfc;
}

.logo-preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.logo-placeholder {
  color: #6b6f80;
  font-size: 13px;
  text-align: center;
}

.hidden-input {
  display: none;
}

.actions {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
}

.error {
  color: #e53935;
  font-size: 13px;
  margin: 8px 0 0 0;
}
</style>
